// src/pages/booking/MyBookingsPage.tsx
// My bookings: upcoming / past, plus a waitlisted section. Actions: cancel,
// reschedule, check-in, check-out, leave a review, and waitlist accept/leave.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import { RescheduleDialog } from '@/features/booking/RescheduleDialog';
import { ReviewDialog } from '@/features/booking/ReviewDialog';
import {
  BOOKING_STATUS_KEYS,
  BOOKING_STATUS_VARIANT,
  errMessage,
  fmtDateTime,
  fmtMoney,
  loadLocalWaitlist,
  makeEntity,
  removeLocalWaitlistEntry,
  type LocalWaitlistEntry,
} from '@/features/booking/lib';
import { ListMyBookings } from '@core/api/endpoints/booking/list_my_bookings';
import { GetBooking } from '@core/api/endpoints/booking/get_booking';
import { CancelBooking } from '@core/api/endpoints/booking/cancel_booking';
import { StartBooking } from '@core/api/endpoints/booking/start_booking';
import { CompleteBooking } from '@core/api/endpoints/booking/complete_booking';
import { LeaveWaitlist } from '@core/api/endpoints/booking/leave_waitlist';
import { AcceptWaitlistOffer } from '@core/api/endpoints/booking/accept_waitlist_offer';
import { BatchGetEntityPayments } from '@core/api/endpoints/payment/batch_get_entity_payments';
import { EntityPaymentPanel } from '@/features/payment/EntityPaymentPanel';
import { entityToUrn } from '@/features/resources/logic';
import type { Booking } from '@core/types/booking';
import type { EntityPaymentEntry } from '@core/types/payment/EntityPaymentEntry';

type Tab = 'upcoming' | 'past';

export const MyBookingsPage = observer(() => {
  const { t, locale } = useI18n();
  const scope = useScope();

  const [tab, setTab] = useState<Tab>('upcoming');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [waitlist, setWaitlist] = useState<LocalWaitlistEntry[]>([]);
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const [rescheduling, setRescheduling] = useState<Booking | null>(null);
  const [reviewing, setReviewing] = useState<Booking | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<Booking | null>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  // Payment aspect per booking, keyed by URN — one batch call for the whole
  // list instead of GetEntityPayment per row (BatchGetEntityPayments).
  const [paymentEntries, setPaymentEntries] = useState<Record<string, EntityPaymentEntry>>({});

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const params = tab === 'upcoming' ? { date_from: now, limit: 100 } : { date_to: now, limit: 100 };
      const res = await ListMyBookings(params);
      const list = res.bookings ?? [];
      setBookings(list);
      const urns = list.map((b) => entityToUrn(b.entity)).filter(Boolean);
      if (urns.length > 0) {
        BatchGetEntityPayments({ host_urns: urns })
          .then((r) => setPaymentEntries(r.entries ?? {}))
          .catch(() => {});
      } else {
        setPaymentEntries({});
      }
    } catch (e) {
      setError(errMessage(e, t('booking.myBookings.loadError')));
    } finally {
      setIsLoading(false);
    }
  }, [scope.productId, tab, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setWaitlist(loadLocalWaitlist().filter((w) => w.productId === scope.productId));
  }, [scope.productId]);

  // The list payload is trimmed — fetch the full record (payment references,
  // linked resources) when a person opens a booking's details.
  const toggleDetails = async (b: Booking) => {
    const id = b.entity?.id ?? '';
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(id);
    setExpandedDetail(null);
    setExpandedLoading(true);
    try {
      const res = await GetBooking(id);
      setExpandedDetail(res.booking ?? null);
    } catch (e) {
      setError(errMessage(e, t('booking.myBookings.loadError')));
    } finally {
      setExpandedLoading(false);
    }
  };

  const doCancel = async () => {
    if (!cancelling?.entity) return;
    setBusyId(cancelling.entity.id);
    try {
      await CancelBooking(cancelling.entity.type_name || 'booking', cancelling.entity.id, {
        target: cancelling.entity,
        initiator: 'CANCELLATION_INITIATOR_CUSTOMER',
        reason: '',
      });
      setCancelling(null);
      load();
    } catch (e) {
      setError(errMessage(e, t('booking.myBookings.cancelError')));
    } finally {
      setBusyId(null);
    }
  };

  const checkIn = async (b: Booking) => {
    if (!b.entity) return;
    setBusyId(b.entity.id);
    try {
      await StartBooking(b.entity.type_name || 'booking', b.entity.id, { target: b.entity });
      load();
    } catch (e) {
      setError(errMessage(e, t('booking.myBookings.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const checkOut = async (b: Booking) => {
    if (!b.entity) return;
    setBusyId(b.entity.id);
    try {
      await CompleteBooking(b.entity.type_name || 'booking', b.entity.id, { target: b.entity });
      load();
    } catch (e) {
      setError(errMessage(e, t('booking.myBookings.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const leaveWaitlist = async (entry: LocalWaitlistEntry) => {
    setBusyId(entry.entryId);
    try {
      await LeaveWaitlist(entry.entryId);
      setWaitlist(removeLocalWaitlistEntry(entry.entryId));
    } catch (e) {
      setError(errMessage(e, t('booking.waitlist.leaveError')));
    } finally {
      setBusyId(null);
    }
  };

  const acceptOffer = async (entry: LocalWaitlistEntry) => {
    setBusyId(entry.entryId);
    try {
      await AcceptWaitlistOffer(entry.entryId, {
        entry: makeEntity(entry.entryId, 'waitlist_entry', { platformId: scope.platformId, productId: entry.productId, tenantId: scope.tenantId }),
      });
      setWaitlist(removeLocalWaitlistEntry(entry.entryId));
      load();
    } catch (e) {
      setError(errMessage(e, t('booking.waitlist.acceptError')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('booking.myBookings.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('booking.myBookings.subtitle')}</p>
        </div>
        <Link to="/booking" className="text-sm text-primary hover:underline">
          {t('booking.catalog.backToCatalog')}
        </Link>
      </div>

      {waitlist.length > 0 && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <p className="text-sm font-medium text-foreground">{t('booking.waitlist.myWaitlist')}</p>
          {waitlist.map((w) => (
            <div key={w.entryId} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{w.resourceName} — #{w.position}</span>
              <div className="flex gap-2">
                {w.status === 'WAITLIST_STATUS_OFFERED' && (
                  <Button size="sm" isLoading={busyId === w.entryId} onClick={() => acceptOffer(w)}>
                    {t('booking.waitlist.accept')}
                  </Button>
                )}
                <Button size="sm" variant="secondary" isLoading={busyId === w.entryId} onClick={() => leaveWaitlist(w)}>
                  {t('booking.waitlist.leave')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div role="tablist" className="flex gap-1 border-b border-border">
        {(['upcoming', 'past'] as Tab[]).map((tb) => (
          <button
            key={tb}
            role="tab"
            aria-selected={tab === tb}
            onClick={() => setTab(tb)}
            className={`px-3 py-2 text-sm ${tab === tb ? 'border-b-2 border-primary font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t(`booking.myBookings.tab.${tb}`)}
          </button>
        ))}
      </div>

      {error && (
        <Alert variant="error">
          {error}
          <Button size="sm" variant="secondary" className="ml-2" onClick={load}>
            {t('booking.retry')}
          </Button>
        </Alert>
      )}

      {isLoading && bookings.length === 0 && (
        <div className="space-y-2" aria-busy="true">
          <div className="h-20 animate-pulse rounded-md bg-muted" />
          <div className="h-20 animate-pulse rounded-md bg-muted" />
        </div>
      )}

      {!isLoading && bookings.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t(`booking.myBookings.empty.${tab}`)}</p>
        </div>
      )}

      <div className="space-y-2">
        {bookings.map((b) => {
          const st = b.status ?? 'BOOKING_STATUS_CONFIRMED';
          const canCancel = st === 'BOOKING_STATUS_CONFIRMED' || st === 'BOOKING_STATUS_PENDING_APPROVAL';
          const canReschedule = st === 'BOOKING_STATUS_CONFIRMED';
          const canCheckIn = st === 'BOOKING_STATUS_CONFIRMED';
          const canCheckOut = st === 'BOOKING_STATUS_IN_PROGRESS';
          const canReview = st === 'BOOKING_STATUS_COMPLETED';
          return (
            <div key={b.entity?.id} className="rounded-md border border-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleDetails(b)}
                      aria-expanded={expandedId === b.entity?.id}
                      className="text-sm font-medium text-foreground hover:underline"
                    >
                      {b.booking_number || t('booking.myBookings.untitled')}
                    </button>
                    <Badge variant={BOOKING_STATUS_VARIANT[st] ?? 'default'} size="sm">
                      {t(`booking.status.${BOOKING_STATUS_KEYS[st]}`)}
                    </Badge>
                    {(() => {
                      const aspect = paymentEntries[entityToUrn(b.entity)]?.aspect;
                      return aspect?.status ? (
                        <Badge variant="secondary" size="sm">{aspect.status}</Badge>
                      ) : null;
                    })()}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {fmtDateTime(b.created_at, locale)} · {fmtMoney(b.total_cents, b.currency, locale)}
                  </p>
                  {b.customer_notes && <p className="mt-1 text-sm text-muted-foreground">{b.customer_notes}</p>}
                </div>
                <div className="flex shrink-0 flex-wrap gap-1.5">
                  {canCheckIn && (
                    <Button size="sm" variant="secondary" isLoading={busyId === b.entity?.id} onClick={() => checkIn(b)}>
                      {t('booking.myBookings.checkIn')}
                    </Button>
                  )}
                  {canCheckOut && (
                    <Button size="sm" variant="secondary" isLoading={busyId === b.entity?.id} onClick={() => checkOut(b)}>
                      {t('booking.myBookings.checkOut')}
                    </Button>
                  )}
                  {canReschedule && (
                    <Button size="sm" variant="secondary" onClick={() => setRescheduling(b)}>
                      {t('booking.myBookings.reschedule')}
                    </Button>
                  )}
                  {canReview && (
                    <Button size="sm" variant="secondary" onClick={() => setReviewing(b)}>
                      {t('booking.myBookings.leaveReview')}
                    </Button>
                  )}
                  {canCancel && (
                    <Button size="sm" variant="danger" onClick={() => setCancelling(b)}>
                      {t('common.cancel')}
                    </Button>
                  )}
                </div>
              </div>
              {expandedId === b.entity?.id && (
                <div className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
                  {expandedLoading && <p>{t('common.loading')}</p>}
                  {!expandedLoading && expandedDetail && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                      <div>
                        <dt className="font-medium text-foreground">{t('booking.myBookings.detail.paid')}</dt>
                        <dd>{fmtMoney(expandedDetail.paid_cents, expandedDetail.currency, locale)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-foreground">{t('booking.myBookings.detail.deposit')}</dt>
                        <dd>{fmtMoney(expandedDetail.deposit_cents, expandedDetail.currency, locale)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-foreground">{t('booking.myBookings.detail.guestCount')}</dt>
                        <dd>{expandedDetail.guest_count}</dd>
                      </div>
                    </dl>
                  )}
                  {!expandedLoading && expandedDetail?.entity && (
                    <div className="mt-3 border-t border-border pt-3">
                      <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-foreground">
                        {t('booking.myBookings.detail.payment')}
                      </p>
                      <EntityPaymentPanel entity={expandedDetail.entity} />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <RescheduleDialog open={!!rescheduling} onClose={() => setRescheduling(null)} booking={rescheduling} onRescheduled={load} />
      <ReviewDialog open={!!reviewing} onClose={() => setReviewing(null)} booking={reviewing} onSubmitted={load} />
      <ConfirmDialog
        open={!!cancelling}
        title={t('booking.myBookings.cancelConfirmTitle')}
        description={t('booking.myBookings.cancelConfirmDescription')}
        destructive
        isConfirming={!!busyId}
        onCancel={() => setCancelling(null)}
        onConfirm={doCancel}
      />
    </div>
  );
});
